/**
 * Settings Screen - Cache Management Actions
 *
 * Extracted from app/(tabs)/settings.tsx (Line Count Refactor 2025-11-21)
 *
 * Handles AsyncStorage and React Query cache clearing with user confirmation.
 * Supports both authenticated (user-specific cache key) and anonymous users.
 *
 * Phase: 1B - Production Code Refactoring
 * Refactored: 2025-11-21 (settings.tsx: 345 → 172 lines)
 *
 * MEDIUM-2 FIX (2025-11-24): Now clears ALL app caches, not just React Query.
 * - React Query persistent cache
 * - Trial prompt state
 * - Quota warnings state
 * - Character progression tracking
 * - Two Voices intro banners
 */

import { QueryClient } from '@tanstack/react-query';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { TFunc } from './types';
import { captureEvent } from '../analytics';

/**
 * All AsyncStorage keys used by the app that should be cleared on cache clear.
 * IMPORTANT: Keep this list synchronized with new AsyncStorage keys added to the app.
 *
 * Keys NOT cleared (preserved):
 * - User authentication state (handled by Supabase)
 * - Language preference (user explicitly set)
 */
const CLEARABLE_CACHE_KEYS = {
  // React Query persistent cache (user-specific)
  queryCache: (userId?: string) =>
    userId ? `DRAMADETECTOR_QUERY_CACHE_${userId}` : 'DRAMADETECTOR_QUERY_CACHE_anonymous',

  // Trial prompt state (tracks when prompts were shown)
  trialPromptState: '@DramaDetector/trialPromptState',

  // Quota warnings state (tracks dismissed warnings)
  quotaWarningsShown: '@DramaDetector/quotaWarningsShown',

  // Character progression tracking (session count for Two Voices reveal)
  analysisSessionCount: '@DramaDetector/analysis_session_count',
  twoVoiceRevealed: '@DramaDetector/two_voice_revealed',
};

export interface CacheActionsParams {
  userId?: string;
  alert: (title: string, message: string, buttons?: any[]) => void;
  t: TFunc;
  tCommon: TFunc;
  queryClient: QueryClient;
}

/**
 * Handles cache clearing with user confirmation.
 *
 * Workflow:
 * 1. Show confirmation alert
 * 2. On confirm: Clear AsyncStorage cache + React Query cache
 * 3. Track analytics event
 * 4. Show success/error alert
 *
 * @param params Cache action parameters
 */
export function handleClearCache(params: CacheActionsParams): void {
  const { userId, alert, t, tCommon, queryClient } = params;

  alert(
    t('cache.confirm_title'),
    t('cache.confirm_message'),
    [
      { text: tCommon('cancel'), style: 'cancel' },
      {
        text: t('cache.clear'),
        style: 'destructive',
        onPress: async () => {
          // Track cache clear action
          captureEvent('feature_used', {
            feature: 'clear_cache',
            screen: 'settings',
            user_tenure_days: 0,
          });

          try {
            // 1. Build list of all cache keys to clear
            const keysToRemove = [
              // React Query persistent cache (user-specific)
              CLEARABLE_CACHE_KEYS.queryCache(userId),
              // Trial and progression state
              CLEARABLE_CACHE_KEYS.trialPromptState,
              CLEARABLE_CACHE_KEYS.quotaWarningsShown,
              CLEARABLE_CACHE_KEYS.analysisSessionCount,
              CLEARABLE_CACHE_KEYS.twoVoiceRevealed,
            ];

            // 2. Clear all persisted caches from AsyncStorage atomically
            await AsyncStorage.multiRemove(keysToRemove);

            // 3. Clear in-memory React Query cache
            queryClient.clear();

            // Log detailed cache clear info for debugging
            console.log('[CacheActions] Cleared caches:', {
              keysCleared: keysToRemove.length,
              userId: userId || 'anonymous',
            });

            alert(tCommon('success'), t('cache.success'));
          } catch (error) {
            alert(
              tCommon('error'),
              t('cache.error', {
                message: error instanceof Error ? error.message : 'Unknown error',
              })
            );
          }
        },
      },
    ]
  );
}
